import { useEffect, useMemo, useRef, useState } from 'react'
import BulkMapBar from './BulkMapBar'
import StateBlock from './StateBlock'
import UnitRow from './UnitRow'
import { errorMessage } from '../lib/api'
import { isMapped, typeLabel } from '../lib/format'
import { useExternalUnitsMap, useSaveMappings } from '../lib/hooks'
import { buildMappingPayload } from '../lib/mappings'
import type { ParkDetail, UnitMapping } from '../lib/types'

export default function UnitsTab({ slug, detail }: { slug: string; detail: ParkDetail }) {
  const [query, setQuery] = useState('')
  const [type, setType] = useState('')
  const [zone, setZone] = useState('')
  const [onlyUnmapped, setOnlyUnmapped] = useState(false)
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const selectAll = useRef<HTMLInputElement>(null)

  // Two instances so a row edit does not leave its result sitting in the bulk bar.
  const rowMapping = useSaveMappings(slug)
  const bulkMapping = useSaveMappings(slug)
  const linkable = detail.integrations.filter((integration) => integration.capabilities.listsUnits)
  const external = useExternalUnitsMap(linkable.map((integration) => integration.id))

  const types = useMemo(() => [...new Set(detail.units.map((unit) => unit.type))].sort(), [detail.units])

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return detail.units.filter((unit) => {
      if (needle !== '' && !unit.number.toLowerCase().includes(needle) && !(unit.name ?? '').toLowerCase().includes(needle)) {
        return false
      }
      if (type !== '' && unit.type !== type) return false
      if (zone !== '' && (unit.zone ?? '') !== zone) return false
      if (onlyUnmapped && isMapped(unit, detail.integrations)) return false
      return true
    })
  }, [detail.units, detail.integrations, query, type, zone, onlyUnmapped])

  const visibleSelected = filtered.filter((unit) => selected.has(unit.id)).length
  useEffect(() => {
    if (selectAll.current) {
      selectAll.current.indeterminate = visibleSelected > 0 && visibleSelected < filtered.length
    }
  }, [visibleSelected, filtered.length])

  const toggleOne = (id: string, on: boolean) => {
    setSelected((current) => {
      const next = new Set(current)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const toggleAll = (on: boolean) => {
    setSelected((current) => {
      const next = new Set(current)
      for (const unit of filtered) {
        if (on) next.add(unit.id)
        else next.delete(unit.id)
      }
      return next
    })
  }

  // The endpoint upserts, so one changed row is one row in the body.
  const applyMapping = (integrationId: string, unitId: string, mapping: UnitMapping | null) => {
    const changes = new Map<string, UnitMapping | null>([[unitId, mapping]])
    rowMapping.mutate({ integrationId, mappings: buildMappingPayload(detail.units, changes) })
  }

  const applyBulk = (integrationId: string, externalTypeId: string | null) => {
    const changes = new Map<string, UnitMapping | null>()
    for (const unit of detail.units) {
      if (!selected.has(unit.id)) continue
      if (externalTypeId === null) changes.set(unit.id, null)
      // Keep a per-unit link if the park already has one; the bulk action only sets the type.
      else changes.set(unit.id, { externalUnitId: unit.mappings[integrationId]?.externalUnitId ?? null, externalTypeId })
    }
    bulkMapping.mutate({ integrationId, mappings: buildMappingPayload(detail.units, changes) })
  }

  if (detail.units.length === 0) {
    return <StateBlock kind="empty" message="Dit park heeft nog geen plaatsen. Importeer ze met app:park:import." />
  }

  const hasIntegrations = detail.integrations.length > 0
  const unmappedCount = detail.units.filter((unit) => !isMapped(unit, detail.integrations)).length

  return (
    <>
      <div className="filters">
        <div className="field">
          <label htmlFor="filter-number">Zoek op nummer of naam</label>
          <input
            id="filter-number"
            type="search"
            value={query}
            placeholder="bijv. 118"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="filter-type">Type</label>
          <select id="filter-type" value={type} onChange={(event) => setType(event.target.value)}>
            <option value="">alle types</option>
            {types.map((key) => (
              <option key={key} value={key}>
                {typeLabel(key)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="filter-zone">Veld</label>
          <select id="filter-zone" value={zone} onChange={(event) => setZone(event.target.value)}>
            <option value="">alle velden</option>
            {detail.zones.map((item) => (
              <option key={item.id} value={item.name}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
        <label className="check">
          <input type="checkbox" checked={onlyUnmapped} onChange={(event) => setOnlyUnmapped(event.target.checked)} />
          alleen ongekoppeld
        </label>
        <span className="muted small">
          {filtered.length} van {detail.units.length} plaatsen
          {hasIntegrations ? ` · ${unmappedCount} nog niet gekoppeld` : ''}
        </span>
      </div>

      {selected.size > 0 && (
        <BulkMapBar
          integrations={detail.integrations}
          external={external}
          count={selected.size}
          pending={bulkMapping.isPending}
          result={bulkMapping.data ?? null}
          error={bulkMapping.isError ? errorMessage(bulkMapping.error) : null}
          onApply={applyBulk}
          onClearSelection={() => setSelected(new Set())}
        />
      )}

      {rowMapping.isError && <p className="inline-error">Koppeling niet opgeslagen: {errorMessage(rowMapping.error)}</p>}
      {rowMapping.isPending && <p className="muted small">Koppeling opslaan…</p>}

      {filtered.length === 0 ? (
        <StateBlock kind="empty" message="Geen plaatsen die aan de filters voldoen." />
      ) : (
        <div className="table-wrap tall">
          <table className="units-table">
            <thead>
              <tr>
                <th scope="col" className="tight">
                  <input
                    ref={selectAll}
                    type="checkbox"
                    checked={filtered.length > 0 && visibleSelected === filtered.length}
                    onChange={(event) => toggleAll(event.target.checked)}
                    aria-label="Selecteer alle zichtbare plaatsen"
                  />
                </th>
                <th scope="col">Nummer</th>
                <th scope="col">Naam</th>
                <th scope="col">Type</th>
                <th scope="col">Veld</th>
                <th scope="col">Kenmerken</th>
                <th scope="col" className="num">Max. pers.</th>
                <th scope="col" className="num">Richtprijs</th>
                {detail.integrations.map((integration) => {
                  const state = external.get(integration.id)
                  return (
                    <th scope="col" key={integration.id}>
                      {integration.label}
                      {state?.loading && <> <span className="spinner" aria-label="Types ophalen" /></>}
                      {state?.error && <div className="inline-error">{state.error}</div>}
                      {state?.hint && <div className="muted small">{state.hint} Vul de ids zelf in.</div>}
                      {!integration.capabilities.listsUnits && (
                        <div className="muted small">Geen lijst; vul de ids zelf in.</div>
                      )}
                    </th>
                  )
                })}
                <th scope="col" className="tight">
                  <span className="visually-hidden">Opslagstatus</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((unit) => (
                <UnitRow
                  key={unit.id}
                  slug={slug}
                  unit={unit}
                  unitTypes={detail.unitTypes ?? []}
                  zones={detail.zones}
                  integrations={detail.integrations}
                  external={external}
                  selected={selected.has(unit.id)}
                  onSelect={toggleOne}
                  onMapping={applyMapping}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
